/**
 * FunctionTestConsole Component
 * 
 * Modal console for testing Cloud Functions.
 * Features:
 * - JSON input for parameters
 * - Execute function via REST API
 * - Display response with timing
 * - Error handling and formatting
 * - Fullscreen support via Portal
 */

import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';

interface FunctionTestConsoleProps {
  /** Function name to test */
  functionName: string;
  /** Parse Server URL */
  parseServerURL: string;
  /** Application ID */
  appId: string;
  /** Master Key (optional, for admin testing) */
  masterKey?: string;
  /** Whether the console is open */
  isOpen: boolean;
  /** Callback to close the console */
  onClose: () => void;
}

interface TestResult {
  success: boolean;
  data: unknown;
  error?: string;
  duration: number;
}

export default function FunctionTestConsole({
  functionName,
  parseServerURL,
  appId,
  masterKey,
  isOpen,
  onClose,
}: FunctionTestConsoleProps) {
  // Input parameters as JSON string
  const [params, setParams] = useState<string>('{\n  \n}');
  // Test result
  const [result, setResult] = useState<TestResult | null>(null);
  // Loading state
  const [isRunning, setIsRunning] = useState(false);

  // Execute the function
  const runFunction = useCallback(async () => {
    setIsRunning(true);
    setResult(null);

    const startTime = performance.now();

    try {
      // Parse the input JSON
      let parsedParams = {};
      try {
        parsedParams = JSON.parse(params);
      } catch {
        setResult({
          success: false,
          data: null,
          error: 'Invalid JSON in parameters',
          duration: 0,
        });
        setIsRunning(false);
        return;
      }

      // Build headers
      const headers: Record<string, string> = {
        'X-Parse-Application-Id': appId,
        'Content-Type': 'application/json',
      };

      // Add master key if available (for admin testing)
      if (masterKey) {
        headers['X-Parse-Master-Key'] = masterKey;
      }

      // Call the function
      const response = await fetch(
        `${parseServerURL}/functions/${functionName}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(parsedParams),
        }
      );

      const duration = performance.now() - startTime;
      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          data,
          duration,
        });
      } else {
        setResult({
          success: false,
          data,
          error: data.error || `HTTP ${response.status}`,
          duration,
        });
      }
    } catch (err) {
      const duration = performance.now() - startTime;
      setResult({
        success: false,
        data: null,
        error: err instanceof Error ? err.message : 'Unknown error',
        duration,
      });
    } finally {
      setIsRunning(false);
    }
  }, [functionName, parseServerURL, appId, masterKey, params]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // REST endpoint for display
  const endpoint = `POST ${parseServerURL}/functions/${functionName}`;

  // Use portal to render outside component hierarchy for proper layering
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-3xl max-h-[90vh] bg-[#1e1e1e] rounded-xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#3c3c3c] bg-[#252526] flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#208AEC]/20 flex items-center justify-center flex-shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#208AEC]">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-medium truncate">Test: {functionName}</h3>
              <code className="text-xs text-white/40 font-mono truncate block">{endpoint}</code>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors flex-shrink-0"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4 min-h-0">
          {/* Parameters Input */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Parameters (JSON)
            </label>
            <div className="rounded-lg overflow-hidden border border-[#3c3c3c]">
              <Editor
                height="120px"
                language="json"
                value={params}
                onChange={(value) => setParams(value || '{}')}
                theme="vs-dark"
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  lineNumbers: 'off',
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  padding: { top: 8, bottom: 8 },
                  scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                  folding: false,
                  glyphMargin: false,
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 0,
                  wordWrap: 'on',
                }}
              />
            </div>
          </div>

          {/* Run Button */}
          <button
            onClick={runFunction}
            disabled={isRunning}
            className={`
              w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
              font-medium text-sm transition-all duration-200
              ${isRunning 
                ? 'bg-[#208AEC]/50 cursor-not-allowed' 
                : 'bg-[#208AEC] hover:bg-[#208AEC]/80'}
              text-white
            `}
          >
            {isRunning ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Running...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Run Function
              </>
            )}
          </button>

          {/* Response */}
          {result && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-white/70">
                  Response
                </label>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-white/40">
                    {result.duration.toFixed(0)}ms
                  </span>
                  <span className={result.success ? 'text-emerald-400' : 'text-red-400'}>
                    {result.success ? '✓ Success' : '✗ Error'}
                  </span>
                </div>
              </div>
              <div className={`
                rounded-lg border overflow-hidden
                ${result.success ? 'border-emerald-500/30' : 'border-red-500/30'}
              `}>
                <Editor
                  height="200px"
                  language="json"
                  value={JSON.stringify(result.data, null, 2)}
                  theme="vs-dark"
                  options={{
                    readOnly: true,
                    minimap: { enabled: false },
                    fontSize: 13,
                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                    lineNumbers: 'off',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    padding: { top: 8, bottom: 8 },
                    scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                    folding: true,
                    glyphMargin: false,
                    lineDecorationsWidth: 0,
                    lineNumbersMinChars: 0,
                    wordWrap: 'on',
                  }}
                />
              </div>
              {result.error && (
                <p className="mt-2 text-sm text-red-400">
                  {result.error}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[#3c3c3c] bg-[#252526] flex items-center justify-between text-xs text-white/40 flex-shrink-0">
          <span>Using {masterKey ? 'Master Key' : 'Application ID'}</span>
          <button
            onClick={() => {
              navigator.clipboard.writeText(endpoint);
            }}
            className="flex items-center gap-1 hover:text-white/70 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
            Copy endpoint
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

